import type { Action, ThunkAction } from '@reduxjs/toolkit';
import type { TypedUseSelectorHook } from 'react-redux';
import { combineSlices, configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { baseApi } from '@app/api/base.api';
import { useDispatch, useSelector } from 'react-redux';
import { showApi } from '../../features/Dashboard/api/showApi';
import { commonDDLApi } from '@app/api/common.ddl.api';
import { showsliceApi } from '../../features/ShowManagement/api/showsliceApi';
import { organizerApi } from '../../features/MastersData/api/organizerAPI';
import { venueApi } from '../../features/MastersData/api/venueAPI';
import { dsvofficeApi } from '../../features/MastersData/api/dsvofficeAPI';
import { workOrderApi } from '../../features/EfairsJob/api/workOrderApi';
import { showaddresssliceApi } from '../../features/ShowManagement/api/showaddresssliceApi';
import { serviceSliceApi } from '../../features/EfairsJob/api/serviceSlice';
import { woserviceApi } from '../../features/EfairsJob/api/woserviceApi';
import { multiServicesSlice } from '../../features/EfairsJob/api/multiServicesSlice';
import { eFairsJobSliceApi } from '../../features/EfairsJob/api/eFairsJobSliceApi';
import { fileAttachmentApiSlice } from '../../features/EfairsJob/api/fileAttachmentApiSlice';
import { recipientApiSlice } from '../../features/EfairsJob/api/recipientApiSlice';
import { trafficJobSliceApi } from '../../features/TrafficManagement/api/trafficJobSlice';
import { trfcEmailTemplateSliceApi } from '../../features/TrafficManagement/api/trfcEmailTemplateSlice';
import { showSelectionApi } from '@app/api/showSelectionSlice';
import { jobServiceApi } from '../../features/ServiceManagement/api/jobServiceAPI';
import { arcnsliceapi } from '../../features/Finance/api/arcn.API';
import { apcnsliceapi } from '../../features/Finance/api/apcn.API';
import { exhibitorApi } from '../../features/ExhibitorManagement/api/exhibitorSliceApi';
import { mastersapi } from '../../features/MastersData/api/mastersapislice';
import { chargeLineAPI } from '../../features/ServiceManagement/api/chargeLineAPI';
import { usersliceApi } from '../../features/UserManagement/api/usersliceApi';
import { userRegistrationApi } from '../../features/LandingPage/api/userRegistrationAPI';
import { storageLabelAPI } from '../../features/ServiceManagement/api/addLabelsAPI';
import { addLabelsForStatusAPI } from '../../features/ServiceManagement/api/addLabelsForStatusAPI';
import { qrcodeLabelApi } from '../../features/ServiceManagement/api/qrcodeLabelApi';
import { sustainabilityRptAPI } from '../../features/ServiceManagement/api/sustainabilityRptAPI';
import { mdgsliceApi } from '../../features/Finance/api/mdgsliceApi';
import { paymentsliceApi } from '../../features/PaymentManagement/api/paymentsliceApi';
import { paymentApi } from '../../features/LandingPage/api/paymentAPI';
import { trackingpodApi } from '../../features/LandingPage/api/trackandtracesliceapi';
import { arapreportsapi } from '../../features/Finance/api/arapreportsapi';
import { customsApi } from '../../features/Customs/api/customs.API';
import { customsApiSlice as efairsJobCustomsApi } from '../../features/EfairsJob/api/customsApiSlice';
import { showWorefApi } from '../../features/ServiceManagement/api/showWorefApi';
import { emailTemplateApi } from '../../features/FormTemplate/api/emailTemplateApi';
import { formTemplateApiSlice } from '../../features/FormTemplate/api/formTemplateApiSlice';
import { commonDMSApi } from '@app/api/documentupload.api';
import { jobServiceSliceApi } from '../../features/ServiceManagement/api/jobServiceSliceApi';
import { invDocUploadApi } from '../../features/Finance/api/invoicedocumentupload.api';
import { userCommentsApi } from '@app/api/userCommentsApi';
import { financereportsapi } from '../../features/Finance/api/financereportsapi';

// ? Add all slices here
const slices = [baseApi, showApi, commonDDLApi, showsliceApi, workOrderApi, showaddresssliceApi, organizerApi, serviceSliceApi, woserviceApi, multiServicesSlice, venueApi,
   eFairsJobSliceApi, trafficJobSliceApi, trfcEmailTemplateSliceApi, showSelectionApi, dsvofficeApi, arcnsliceapi, apcnsliceapi, jobServiceApi, exhibitorApi, mastersapi, 
   chargeLineAPI, usersliceApi, userRegistrationApi, storageLabelAPI, mdgsliceApi, sustainabilityRptAPI, qrcodeLabelApi, addLabelsForStatusAPI, paymentsliceApi, paymentApi,
  trackingpodApi, arapreportsapi, customsApi, efairsJobCustomsApi, showWorefApi, emailTemplateApi, formTemplateApiSlice, commonDMSApi, jobServiceSliceApi, fileAttachmentApiSlice, 
  recipientApiSlice, invDocUploadApi,userCommentsApi, financereportsapi];// Combine all slices into one array
const rootReducer = combineSlices(...slices);

// Infer the `RootState` type from the root reducer
export type RootState = ReturnType<typeof rootReducer>;

export const initStore = (preloadedState?: Partial<RootState>) => {
  const store = configureStore({
    reducer: rootReducer,

    middleware: (getDefaultMiddleware) => {
      return getDefaultMiddleware().concat(
        baseApi.middleware,
        showApi.middleware, // ? Add showApi middleware
        commonDDLApi.middleware, // ? Add commonDDLApi middleware
        showsliceApi.middleware, // ? Add showsliceApi middleware
        organizerApi.middleware, // ? Add organizerApi middleware
        workOrderApi.middleware, // ? Add workOrderApi middleware
        showaddresssliceApi.middleware, // ? Add showaddresssliceApi middleware
        eFairsJobSliceApi.middleware, // ? Add eFairsJobSliceApi middleware
        serviceSliceApi.middleware, // ? Add workOrderApi middleware
        woserviceApi.middleware,
        multiServicesSlice.middleware, // ? Add woserviceApi middleware
        venueApi.middleware, // ? Add venueApi middleware
        trafficJobSliceApi.middleware, // ? Add trafficJobSliceApi middleware
        trfcEmailTemplateSliceApi.middleware, // ? Add trfcEmailTemplateSliceApi middleware
        showSelectionApi.middleware, // ? Add showSelectionApi middleware 
        dsvofficeApi.middleware, // ? Add dsvofficeApi middleware        arcnsliceapi.middleware,
        jobServiceApi.middleware, exhibitorApi.middleware,
        arcnsliceapi.middleware,
        apcnsliceapi.middleware,
        mastersapi.middleware, // ? Add mastersapi middleware
        chargeLineAPI.middleware, // ? Add chargeLineAPI middleware  
        usersliceApi.middleware, // ? Add usersliceApi middleware
        userRegistrationApi.middleware, // ? Add userRegistrationApi middleware  
        storageLabelAPI.middleware, // ? Add storageLabelAPI middleware
        addLabelsForStatusAPI.middleware, // ? Add addLabelsForStatusAPI middleware
        mdgsliceApi.middleware, // ? Add mdgsliceApi middleware
        sustainabilityRptAPI.middleware, // ? Add sustainabilityRptAPI middleware
        qrcodeLabelApi.middleware, // ? Add qrcodeLabelApi middleware
        paymentsliceApi.middleware, // ? Add paymentsliceApi middleware
        paymentApi.middleware, // ? Add paymentApi middleware (LandingPage)
        trackingpodApi.middleware, // ? Add trackingpodApi middleware
        arapreportsapi.middleware, // ? Add arapreportsapi middleware
        customsApi.middleware, // ? Add customsApi middleware
        efairsJobCustomsApi.middleware,
        showWorefApi.middleware, // ? Add showWorefApi middleware
        emailTemplateApi.middleware, // ? Add emailTemplateApi middleware
        formTemplateApiSlice.middleware, // ? Add formTemplateApiSlice middleware
        commonDMSApi.middleware, // ? Add commonDMSApi middleware
        jobServiceSliceApi.middleware, // ? Add jobServiceSliceApi middleware
        fileAttachmentApiSlice.middleware, // ? Add fileAttachmentApiSlice middleware
        recipientApiSlice.middleware, // ? Add recipientApiSlice middleware
        invDocUploadApi.middleware, // ? Add invDocUploadApi middleware
        userCommentsApi.middleware, // ? Add userCommentsApi middleware
        financereportsapi.middleware // ? Add financereportsapi middleware
      );
    },
    preloadedState,
  });

  // Optional but useful for refetch behaviors
  setupListeners(store.dispatch);

  return store;
};

export const store = initStore();

// Infer the type of `store`
export type AppStore = typeof store;
// Infer the `AppDispatch` type from the store itself
export type AppDispatch = AppStore['dispatch'];
export type AppThunk<ThunkReturnType = void> = ThunkAction<
  ThunkReturnType,
  RootState,
  unknown,
  Action
>;
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
